import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Chip, CircularProgress,
} from '@mui/material';
import {
  Circle as DotIcon,
  ArrowDownward as ScrollDownIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import billingService from '../services/billingService';
import { useDeploymentWebSocket } from '../contexts/DeploymentWebSocketContext';
import { toServerDate } from '../timezone';

const statusDotColor = {
  STARTED: '#2196f3',
  IN_PROGRESS: '#ff9800',
  SUCCESS: '#4caf50',
  FAILED: '#f44336',
};

/**
 * Terminal-style deployment log viewer with real-time WebSocket streaming.
 *
 * Props:
 *   nodeId    - (optional) filter to specific node; null = all nodes
 *   nodeName  - display name for the header
 *   maxLines  - max log lines to retain (default 500)
 */
const DeploymentLogViewer = ({ nodeId, nodeName, maxLines = 500 }) => {
  const [logEntries, setLogEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);
  const logContainerRef = useRef(null);
  const { connected, onDeploymentUpdate, onNodeDeploymentUpdate } = useDeploymentWebSocket();

  // Fetch historical logs on mount
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const res = nodeId
          ? await billingService.getNodeDeploymentLogs(nodeId)
          : await billingService.getAllDeploymentLogs();
        const historical = (res.data || [])
          .reverse()
          .map(log => ({ ...log, isHistorical: true }));
        setLogEntries(historical);
      } catch (err) {
        console.error('Failed to fetch historical logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [nodeId]);

  // Subscribe to real-time WebSocket events
  useEffect(() => {
    if (!connected) return;

    const handler = (event) => {
      setLogEntries(prev => {
        const newEntries = [...prev, { ...event, isHistorical: false }];
        return newEntries.length > maxLines ? newEntries.slice(-maxLines) : newEntries;
      });
    };

    const unsub = nodeId
      ? onNodeDeploymentUpdate(nodeId, handler)
      : onDeploymentUpdate(handler);

    return unsub;
  }, [connected, nodeId, onDeploymentUpdate, onNodeDeploymentUpdate, maxLines]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logEntries, autoScroll]);

  // Detect manual scroll up to pause auto-scroll
  const handleScroll = useCallback(() => {
    if (!logContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 40;
    setAutoScroll(atBottom);
  }, []);

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
      const d = toServerDate(ts);
      if (!d) return ts;
      return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return ts;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      {/* Terminal header bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 0.5,
        bgcolor: '#1a1a2e', borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid #333',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5f57' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#28c840' }} />
          <Typography variant="caption" sx={{ color: '#888', ml: 1, fontFamily: 'monospace' }}>
            deployment-logs {nodeName ? `— ${nodeName}` : '— all nodes'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {connected ? (
            <Chip label="LIVE" size="small" sx={{
              bgcolor: '#4caf50', color: '#fff', fontSize: '0.65rem', height: 20,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.6 },
              },
            }} />
          ) : (
            <Chip label="OFFLINE" size="small" sx={{
              bgcolor: '#666', color: '#ccc', fontSize: '0.65rem', height: 20,
            }} />
          )}
          <Tooltip title={autoScroll ? 'Auto-scroll ON (click to pause)' : 'Auto-scroll OFF (click to resume)'}>
            <IconButton size="small" onClick={() => setAutoScroll(!autoScroll)} sx={{ color: '#888' }}>
              {autoScroll ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {!autoScroll && (
            <Tooltip title="Jump to bottom">
              <IconButton size="small" onClick={() => {
                setAutoScroll(true);
                if (logContainerRef.current) {
                  logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
                }
              }} sx={{ color: '#888' }}>
                <ScrollDownIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Terminal body */}
      <Box
        ref={logContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1, overflow: 'auto',
          bgcolor: '#0d1117', color: '#c9d1d9',
          fontFamily: '"Cascadia Code", "Fira Code", "JetBrains Mono", "Consolas", monospace',
          fontSize: '0.78rem', lineHeight: 1.6,
          p: 1.5, borderRadius: '0 0 8px 8px',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { bgcolor: '#161b22' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#30363d', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#484f58' },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#58a6ff' }} />
          </Box>
        ) : logEntries.length === 0 ? (
          <Typography sx={{ color: '#484f58', fontFamily: 'inherit', fontStyle: 'italic' }}>
            No deployment logs yet. Logs will appear here when a deployment starts...
          </Typography>
        ) : (
          logEntries.map((entry, idx) => (
            <Box key={entry.id || `ws-${idx}`} sx={{
              display: 'flex', gap: 1, py: 0.2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
              opacity: entry.isHistorical ? 0.7 : 1,
            }}>
              {/* Timestamp */}
              <Typography component="span" sx={{
                color: '#484f58', fontFamily: 'inherit', fontSize: 'inherit',
                whiteSpace: 'nowrap', minWidth: 70, flexShrink: 0,
              }}>
                {formatTimestamp(entry.createdAt || entry.timestamp)}
              </Typography>

              {/* Status dot */}
              <DotIcon sx={{
                fontSize: 10, mt: '5px', flexShrink: 0,
                color: statusDotColor[entry.status] || '#888',
              }} />

              {/* Step name */}
              {entry.stepName && (
                <Typography component="span" sx={{
                  color: '#58a6ff', fontFamily: 'inherit', fontSize: 'inherit',
                  whiteSpace: 'nowrap', minWidth: 80, flexShrink: 0,
                }}>
                  [{entry.stepName}]
                </Typography>
              )}

              {/* Node name (when showing all nodes) */}
              {!nodeId && entry.nodeName && (
                <Typography component="span" sx={{
                  color: '#d2a8ff', fontFamily: 'inherit', fontSize: 'inherit',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {entry.nodeName}
                </Typography>
              )}

              {/* Message */}
              <Typography component="span" sx={{
                fontFamily: 'inherit', fontSize: 'inherit',
                color: entry.status === 'FAILED' ? '#f85149'
                     : entry.status === 'SUCCESS' ? '#3fb950'
                     : '#c9d1d9',
                wordBreak: 'break-word',
              }}>
                {entry.message}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default DeploymentLogViewer;
